import { useEffect, useState } from "react";
import { Package, Loader2, Rocket, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";

// Admin: bump the Python SDK version and republish to PyPI in one click.
export default function SdkPublishCard() {
  const [info, setInfo] = useState(null);
  const [bump, setBump] = useState("patch");
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState("");
  const load = () => api.get("/sdk/python/version").then(({ data }) => setInfo(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!info) return null;

  const publish = async () => {
    setBusy(true); setLog("");
    try {
      const { data } = await api.post("/sdk/python/publish", { bump, repository: "pypi" });
      setLog(data.log);
      toast.success(`musicflow-studioai ${data.version} publié sur PyPI`);
      await load();
    } catch (e) { const d = e.response?.data?.detail; setLog(typeof d === "string" ? d : ""); toast.error(formatApiError(d) || "Publication impossible."); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-5 space-y-3" data-testid="sdk-publish-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Package size={16} className="text-primary" />
          <div>
            <div className="font-semibold text-sm">Python SDK sur PyPI</div>
            <div className="text-xs text-muted-foreground font-mono">pip install musicflow-studioai · version actuelle <span className="text-primary" data-testid="sdk-current-version">{info.version}</span></div>
          </div>
        </div>
        <a href={info.pypi_url} target="_blank" rel="noreferrer" className="text-xs inline-flex items-center gap-1 text-primary hover:underline" data-testid="sdk-pypi-link"><ExternalLink size={12} /> Voir sur PyPI</a>
      </div>
      {info.is_admin ? (
        <div className="flex flex-wrap items-center gap-2">
          <Select value={bump} onValueChange={setBump}>
            <SelectTrigger className="h-9 w-56 text-xs bg-background border-border" data-testid="sdk-bump-select"><SelectValue /></SelectTrigger>
            <SelectContent className="glass border-border">
              {["patch", "minor", "major"].map((p) => <SelectItem key={p} value={p}>{p} → v{info.next[p]}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={publish} disabled={busy || !info.can_publish} className="bg-primary text-primary-foreground hover:bg-primary/90 gap-2" data-testid="sdk-publish-btn" title={info.can_publish ? "Build + twine check + upload PyPI" : "PYPI_TOKEN manquant dans backend/.env"}>
            {busy ? <Loader2 className="animate-spin" size={15} /> : <Rocket size={15} />} Publier v{info.next[bump]} sur PyPI
          </Button>
          {!info.can_publish && <span className="text-xs text-amber-400" data-testid="sdk-token-missing">PYPI_TOKEN manquant</span>}
        </div>
      ) : (
        <div className="text-xs text-muted-foreground" data-testid="sdk-publish-admin-only">La publication est réservée aux administrateurs.</div>
      )}
      {info.published?.length > 0 && (
        <div className="text-[11px] font-mono text-muted-foreground space-y-0.5" data-testid="sdk-publish-history">
          {info.published.slice().reverse().map((p) => <div key={p.at}>v{p.version} · {p.repository} · {new Date(p.at).toLocaleString("fr-FR")} · {p.by}</div>)}
        </div>
      )}
      {log && <pre className="text-[10px] font-mono bg-background border border-border rounded-lg p-3 max-h-48 overflow-auto whitespace-pre-wrap" data-testid="sdk-publish-log">{log}</pre>}
    </div>
  );
}
